/**
 * Auth context + helpers. Split out from AuthContext.tsx so the latter
 * file only exports the AuthProvider component — keeps React Refresh /
 * Vite HMR happy (it warns when a file exports both components and
 * non-component values).
 */

import { createContext, useContext } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

export type Tier = "free" | "paid";

export interface AuthValue {
  user: { sub: string; email: string } | null;
  tier: Tier;
  idToken: string | null;
  loading: boolean;
  error: { code: string; message: string } | null;
  /** Redirect to the Cognito Managed Login chooser (Google + email/password). */
  signIn: () => Promise<void>;
  /** Sign out + revoke tokens. */
  signOut: () => Promise<void>;
  /**
   * Re-read tokens from Amplify. Pass `true` to force a new id token (used
   * after the Stripe upgrade so the paid tier claim lands immediately).
   */
  refresh: (forceRefresh?: boolean) => Promise<void>;
}

export const AuthContext = createContext<AuthValue | null>(null);

// Sign-out latch. `signOut()` clears the SPA's local tokens (user → null) a
// moment before the browser navigates to Cognito's hosted logout. Without this,
// RequireAuth sees user=null and fires signInWithRedirect() first — and because
// the hosted session cookie is still live, Cognito silently re-authenticates and
// bounces the user back ("sign out just refreshes the screen"). handleSignOut
// sets this so RequireAuth holds off during the sign-out navigation. It lives
// for the life of the page — the logout navigation unloads it.
let _signingOut = false;
export function beginSignOut(): void {
  _signingOut = true;
}
export function isSigningOut(): boolean {
  return _signingOut;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/**
 * Lazy token getter used by api/client.ts. Returns the current ID token
 * (refreshing via Amplify if needed) or null if unauthenticated. Async
 * so callers can await it before issuing the fetch.
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}
